import { ReactElement } from 'react';
import create from 'zustand';

import { Api } from './api';
import { auditBatch } from './audit-batch-api';
import { DateInline, Tags, Text } from './controls';
import { multiSelectFormCriteria } from './dashboard-search-api';
import {
  DateRange,
  utcDateString
} from './date-time';
import { Provider, knownCodingstates } from './domain';
import { Control, FormApi, formApi, providerField } from './form';
import { HBox } from './layout';
import { PageApi } from './paged-table';
import { getStoredOrgUrlParams } from './url-params';
import {
  SearchPredicate,
  VisitSearchField,
  VisitSearchQuery,
  auditIdField,
  auditRuleCreatedWhoField,
  auditTypeField,
  chartOwnerLastField,
  flagCommentsField,
  flagDescriptionField,
  flagEmployeeLastField,
  flagField,
  getVisits,
  locationField,
  mrnField,
  ownerFirstField,
  ownerLastField,
  patientFirstField,
  patientLastField,
  registrationDateField,
  serviceDateField,
  visitAccountField,
  visitDepartmentField,
  visitFirstDateInCodingField,
  visitInsuranceField,
  worksheetIdField,
  worksheetQueue,
  worksheetStateField,
  worksheetTypeField
} from './visit-queue';
import { Feature, currentWorkflow } from './workflow-api';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { userProfileApi } from '../api/user-profile-api';
export interface VisitSearchFieldCriteria {
  field: VisitSearchField;
  form?: FormApi;
  color?: string;
  label?: string;
  icon?: ReactElement;
  editable?: boolean;
  dateTime?: boolean;
  api?: VisitSearchApi;
  startDate?: Date;
  endDate?: Date;
  valueLabel?: (v?: any) => string;
  editor?: (c: VisitSearchFieldCriteria) => JSX.Element;
  variant?: string;
  subComponent?: (c: Control) => JSX.Element;
  onChange?: (values: any) => void;
}

type VisitSearchConfiguration = {
  query: VisitSearchQuery;
  fields: VisitSearchFieldCriteria[];
  filters: VisitSearchFieldCriteria[];
  sort: VisitSearchFieldCriteria[];
  features?: Feature[];
};

type VisitSearchData = VisitSearchConfiguration & {
  selectedFilter: VisitSearchFieldCriteria;
  openFilter: boolean;
  period: PeriodSearch;
  advancedActive: boolean;
  providers: Provider[];
  stateFilters: { label: string; name: string }[];
  pendingNavigation?: boolean;
};

interface PeriodSearch {
  period?: DateRange;
  search?: string;
}

type VisitSearchActions = {
  setQuery(v: Partial<VisitSearchQuery>): void;
  updateOrAddFilter(field: VisitSearchField): VisitSearchField;
  updateOrAddSort(field: VisitSearchField): VisitSearchField;
  updateColumnSort(field: VisitSearchField): VisitSearchField;
  search(): void;
  reset(): void;
  makeSortable(): void;
  setServiceDate(range: DateRange): void;
  hasFilter: (c: VisitSearchFieldCriteria) => boolean;
  openFilter: (c: VisitSearchFieldCriteria) => void;
  closeFilter: () => void;
  deleteFilter: (c: VisitSearchFieldCriteria) => void;
  addFilter: (c: VisitSearchFieldCriteria) => void;
  updateFilter: (c: VisitSearchFieldCriteria) => void;
  addSort: (c: VisitSearchFieldCriteria) => void;
  updateSort: (c: VisitSearchFieldCriteria) => void;
  hasSort: (c: VisitSearchFieldCriteria) => boolean;
  deleteSort: (c: VisitSearchFieldCriteria) => void;
};

type VisitSearchForms = {
  predicate: FormApi<{ predicate: 'OR' | 'AND' }>;
};
export type VisitSearchApi = Api<
  VisitSearchConfiguration,
  VisitSearchData,
  VisitSearchActions,
  VisitSearchForms
>;

const params = getStoredOrgUrlParams();

function searchFieldCriteria(
  field: VisitSearchField,
  props: Partial<VisitSearchFieldCriteria>
): Partial<VisitSearchFieldCriteria> {
  return {
    field,
    ...props,
  };
}


function textForm(
  criteria: Partial<VisitSearchFieldCriteria>,
  subComponent?: (c: Control) => JSX.Element
): VisitSearchFieldCriteria {
  const { field } = criteria;

  let selectedField: VisitSearchFieldCriteria;

  const form = formApi({
    values: {
      control: '',
    },
    fields: {
      control: {
        id: (field?.fieldName || 'text') + '-input',
        required: true,
        label: criteria.label,
        autoFocus: true,
        onEnter: () => {
          form.submit();
        },
      },
    },
    valid: () => true,
    onSubmit: (v) => {
      field.stringFilter = (v.control || '').trim();
      if (selectedField && form.valid.getState().value) {
        selectedField.api.actions.addFilter(selectedField);
      }
    },
  });
  const { control } = form.controls;
  function reset() {
    control.value.setState({ value: null });
  }
  return {
    ...criteria,
    ...{
      form,
      control,
      reset,
      valueLabel: () => control.value.getState().value || '',
      editor: (v: VisitSearchFieldCriteria) => {
        selectedField = v;
        return (
          <>
            {subComponent ? <>
              <Text {...control} />
              {subComponent(control)}
            </> :
              <Text {...control} />}

          </>
        )
      },
    },
  } as VisitSearchFieldCriteria;
}

function mutliSelectForm(
  criteria: Partial<VisitSearchFieldCriteria>
): VisitSearchFieldCriteria {
  const { field } = criteria;

  let selectedField: VisitSearchFieldCriteria;

  const form = formApi({
    values: {
      control: [],
    },
    fields: {
      control: {
        id: (field?.fieldName || 'text') + '-input',
        required: true,
        label: criteria.label,
        list: knownCodingstates,
        listLabel: (v) => v.label,
        autoFocus: true,
        style: { width: undefined },
        onEnter: () => {
          form.submit();

          if (selectedField && form.valid.getState().value) {
            selectedField.api.actions.addFilter(selectedField);
          }
        },
      },
    },
    valid: () => true,
    onSubmit: (v) => {
      field.stringFilter = (v.control || []).map((u) => u.name).join(',');
    },
  });
  const { control } = form.controls;
  function reset() {
    if (field) {
       field.stringFilter = null;
    }
    control.value.setState({ value: [] });
  }
  return {
    ...criteria,
    ...{
      form,
      control,
      reset,
      valueLabel: () =>
        (control.value.getState().value || []).map((v) => v.label).join(','),
      editor: (v: VisitSearchFieldCriteria) => {
        selectedField = v;

        if (v.api) {
          const list = v.api.data.getState().stateFilters;

          if (list?.length) {
            control.list.setState({ value: list });
          }
        }
        return <Tags {...control} />;
      },
    },
  } as VisitSearchFieldCriteria;
}

function personForm(
  criteria: Partial<VisitSearchFieldCriteria>
): VisitSearchFieldCriteria {
  const { field } = criteria;

  let selectedField: VisitSearchFieldCriteria;

  const form = formApi({
    values: {
      control: [],
    },
    fields: {
      control: providerField([], {
        id: (field?.fieldName || 'text') + '-input',
        required: true,
        label: criteria.label,
        autoFocus: true,
        style: { width: undefined },
        onEnter: () => {
          form.submit();

          if (selectedField && form.valid.getState().value) {
            selectedField.api.actions.addFilter(selectedField);
          }
        },
      }),
    },
    valid: () => true,
    onSubmit: (v) => {
      field.stringFilter = (v.control || []).map((u) => u.lastName).join(',');
    },
  });
  const { control } = form.controls;
  function reset() {
    control.value.setState({ value: [] });
  }
  return {
    ...criteria,
    ...{
      form,
      control,
      reset,
      // valueLabel: () => control.value.getState().value && control.value.getState().value.lastName || '',
      valueLabel: () =>
        (control.value.getState().value || []).map((v) => (v.lastName || v.label)).join(','),
      editor: (v: VisitSearchFieldCriteria) => {
        selectedField = v;
        if (v.api) {
          control.list.setState({
            value: v.api.data.getState().providers,
          });
        }
        return <Tags {...control} />;
      },
    },
  } as VisitSearchFieldCriteria;
}

function dateForm(
  criteria: Partial<VisitSearchFieldCriteria>
): VisitSearchFieldCriteria {
  const { dateTime, field } = criteria;

  let selectedCriteria: VisitSearchFieldCriteria;

  const onEnter = () => {
    form.submit();
    if (selectedCriteria && form.valid.getState().value) {
      selectedCriteria.api.actions.addFilter(selectedCriteria);
    }
  };

  function onChange() {

    const field = selectedCriteria?.field;


    if (!field) {
      return;
    }
    const st = start.value.getState().value;
    const en = end.value.getState().value;

    if (!(st && en)) {
      return;
    }

    const stUtc = utcDateString(st);
    const endUtc = utcDateString(en);



    if (!stUtc || !endUtc) {
      return;
    }

    field.startDate = stUtc;
    field.endDate = endUtc;

    form.values.setState({ ...({ start: st, end: en } as any) });
  }

  const form = formApi({
    values: {
      start: null,
      end: new Date(),
    },
    fields: {
      start: {
        id: (field?.fieldName || 'text') + 'start-input',
        autoFocus: true,
        required: true,
        label: 'Start',
        onChange,
        onEnter,
      },
      end: {
        id: (field?.fieldName || 'text') + 'end-input',
        required: true,
        label: 'End',
        onChange,
        onEnter,
      },
    },
    valid: (v) => !!v.start && !!v.end,
    onSubmit: () => {
      onChange();
    },
  });

  const { start, end } = form.controls;
  function reset() {
    start.value.setState({ value: null });
  }
  return {
    ...criteria,
    ...{
      form,
      control: end,
      reset,
      valueLabel: (v: VisitSearchFieldCriteria) => {
        const c = selectedCriteria || v;
        return `${c.field.startDate || ''} to ${c.field.endDate || ''}`;
      },
      editor: (v: VisitSearchFieldCriteria) => {
        selectedCriteria = v;
        const { startDate, endDate } = v;
        if (startDate) {
          start.value.setState({ value: startDate });
        }
        if (endDate) {
          end.value.setState({ value: endDate });
        }
        return (
          <HBox>
            <DateInline {...start} />
            <DateInline {...end} />
          </HBox>
        );
      },
    },
  } as VisitSearchFieldCriteria;
}

const locationCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(locationField, {
      ...{ label: 'Location', editable: true },
      ...props,
    }) as any,
    []
  );

const accountCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(visitAccountField, {
      ...{ label: 'Account', editable: true },
      ...props,
    })
  );

const registrationDateCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  dateForm(
    searchFieldCriteria(registrationDateField, {
      ...{ label: 'Registration Date', editable: true, dateTime: true },
      ...props,
    })
  );

const serviceDateCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  dateForm(
    searchFieldCriteria(serviceDateField, {
      ...{ label: 'Service', editable: true },
      ...props,
    })
  );

const mrnCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(mrnField, {
      ...{ label: 'MRN', editable: true },
      ...props,
    })
  );

const auditIdCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(auditIdField, {
      ...{ label: 'AuditId', editable: true },
      ...props,
    }) as any,
    []
  );

const auditTypeCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(auditTypeField, {
      ...{ label: 'AuditType', editable: true },
      ...props,
    })
  );

const worksheetTypeCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(worksheetTypeField, {
      ...{ label: 'WorksheetType', editable: true },
      ...props,
    })
  );

const firstDateInCodingCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  dateForm(
    searchFieldCriteria(visitFirstDateInCodingField, {
      ...{ label: 'Age', editable: true },
      ...props,
    })
  );

const worksheetStateCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  mutliSelectForm(
    searchFieldCriteria(worksheetStateField, {
      ...{ label: 'State', editable: true },
      ...props,
    })
  );

const worksheetIdCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(worksheetIdField, {
      ...{ label: 'Worksheet Id', editable: true },
      ...props,
    })
  );

const departmentCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(visitDepartmentField, {
      ...{ label: 'Department', editable: true },
      ...props,
    }) as any,
    []
  );



const ownerLastCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  personForm(
    searchFieldCriteria(ownerLastField, {
      ...{ label: 'Provider', editable: true },
      ...props,
    })
  );

export const flagCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(flagField, {
      ...{ label: 'Flag', editable: true },
      ...props,
    })
  );

export const flagDescriptionCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(flagDescriptionField, {
      ...{ label: 'Flag Description', editable: true },
      ...props,
    })
  );

export const flagCommentsCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(flagCommentsField, {
      ...{ label: 'Flag Comments', editable: true },
      ...props,
    })
  );
export const flagEmployeeLastCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(flagEmployeeLastField, {
      ...{ label: 'Flag Employee (last name)', editable: true },
      ...props,
    })
  );

const patientLastCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(patientLastField, {
      ...{ label: 'Patient', editable: true },
      ...props,
    })
  );

const insuranceCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(visitInsuranceField, {
      ...{ label: 'Insurance', editable: true },
      ...props,
    })
  );

const chartOwnerCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(chartOwnerLastField, {
      ...{ label: params.activity === 'CODEREVIEW' ? 'Reviewed (last name)' : 'Audited (last name)', editable: true },
      ...props,
    })
  );


const checkIsSelected = (c: Control) => {
  return c.value.getState().value === userProfileApi.data.getState().user.principalId;
}

const checkBoxMyAudits = (c: Control) => {

  return (
    <FormControlLabel
      label="My Audits"
      control={
        <Checkbox
          onChange={(e) => {
            if (e.target.checked) {
              c.value.setState({ value: userProfileApi.data.getState().user.principalId });
            } else {
              c.value.setState({ value: '' });
            }
          }} />

      } />
  );
}

const auditRuleCreteWhoCriteria = (props?: Partial<VisitSearchFieldCriteria>) =>
  textForm(
    searchFieldCriteria(auditRuleCreatedWhoField, {
      ...{ label: 'Created By', editable: true },
      ...props,
    }),
    (control: Control) => (checkBoxMyAudits(control))
  );
const serviceDate = serviceDateCriteria();

export const auditSearchCriteria = [
  accountCriteria(),
  firstDateInCodingCriteria(),
  auditIdCriteria(),
  auditTypeCriteria(),
  chartOwnerCriteria(),
  departmentCriteria(),
  locationCriteria(),
  mrnCriteria(),
  serviceDate,
  worksheetStateCriteria(),
  patientLastCriteria(),
  ownerLastCriteria(),
  worksheetTypeCriteria(),
  auditRuleCreteWhoCriteria(),
  flagCriteria(),
  flagDescriptionCriteria(),
  flagCommentsCriteria(),
  flagEmployeeLastCriteria(),
];

export const visitSearchCriteria = createVisitsSearchFieldCriteria();

function createVisitsSearchFieldCriteria() {
  let criteria: VisitSearchFieldCriteria[] = [
    firstDateInCodingCriteria(),
    worksheetStateCriteria(),
    accountCriteria(),
    mrnCriteria(),
    serviceDate,
    patientLastCriteria(),
    ownerLastCriteria(),
    insuranceCriteria(),
    worksheetIdCriteria(),
    flagCriteria(),
    flagDescriptionCriteria(),
    flagCommentsCriteria(),
    flagEmployeeLastCriteria(),
  ];

  if (params.activity === 'CODEREVIEW') {
    criteria.push(chartOwnerCriteria());
  }

  return criteria;


}

function visitSearchConfiguration(
  conf?: Partial<VisitSearchConfiguration>
): VisitSearchConfiguration {
  return {
    ...{
      query: {},
      fields: visitSearchCriteria,
      filters: [],
      sort: [],
    },
    ...conf,
  };
}

function auditSearchConfiguration(
  conf?: Partial<VisitSearchConfiguration>
): VisitSearchConfiguration {
  return {
    ...{
      query: {},
      fields: auditSearchCriteria as any,
      filters: [],
      sort: [],
    },
    ...conf,
  };
}

const data = create<VisitSearchData>(() => ({
  fields: [],
  query: {},
  filters: [],
  sort: [],
  selectedFilter: null,
  openFilter: false,
  period: null,
  advancedActive: false,
  providers: [],
  stateFilters: knownCodingstates,
  pendingNavigation: false,
}));

export function visitSearchApi(
  page?: PageApi,
  isAudit?: boolean,
): VisitSearchApi {
  const configuration =
    isAudit === true ? auditSearchConfiguration() : visitSearchConfiguration();

  data.setState({ fields: configuration.fields });

  const fetch = getVisits().getState().fetch;

  const search = async () => {
    refreshQuery();
    const query = data.getState().query;
    if (!query.locationCode) {
      return Promise.resolve({}) as any;
    }
    await fetch(query);
  };

  if (page) {
    worksheetQueue.getState().nextResult = () => {
      const query = refreshQuery();
      query.pageNumber += 1;
      page.setState({ pageNumber: query.pageNumber });
      data.setState({ query });
      return search();
    };
  }

  function hasField(
    list: VisitSearchFieldCriteria[],
    v: VisitSearchFieldCriteria
  ): boolean {
    return list.filter((u) => u.label === v.label).length > 0;
  }

  const hasFilter = (v: VisitSearchFieldCriteria) =>
    hasField(data.getState().filters, v);
  const hasSort = (v: VisitSearchFieldCriteria) =>
    hasField(data.getState().sort, v);

  const refreshFields = () => ({ fields: data.getState().fields.slice() });
  const refreshSort = () => ({ sort: data.getState().sort.slice() });

  function updateOrAdd(
    list: VisitSearchFieldCriteria[],
    field: VisitSearchField
  ): VisitSearchField {
    let item = list.filter((u) => u.field.fieldName === field.fieldName)[0];
    if (!item) {
      item = data
        .getState()
        .fields.filter((u) => u.field.fieldName === field.fieldName)[0];
      if (!item) {
        throw new Error(
          field.fieldName + ' is not in the visit search configuration'
        );
      }
      list.push(item);
    }
    item.field = { ...item.field, ...field };


    return item.field;
  }

  const updateOrAddFilter = (field: VisitSearchField) => {
    const v = updateOrAdd(data.getState().filters, field);
    refreshSort();
    return v;
  };

  const updateOrAddSort = (field: VisitSearchField) => {

    const v = updateOrAdd(data.getState().sort, field);
    makeSortable();
    refreshFields();
    return v;
  };

  let currentColumnSort: VisitSearchField;
  let currentPredicate: SearchPredicate = 'OR';

  const refreshQuery = () => {
    const query: VisitSearchQuery = data.getState().query || {};
    const predicate = currentPredicate || 'OR';
    const advanced = data.getState().advancedActive;
    const { search } = { ...data.getState().period };

    query.queryString = search ? search : undefined;

    const filters = data.getState().filters;
    if (advanced || filters?.length) {
      query.filters = {
        predicate,
        fields: filters.map((v) => v.field),
      };
      query.sort = data.getState().sort.map((v) => ({
        fieldName: v.field.fieldName,
        sortOrder: isNaN(v.field.sortOrder) ? 1 : v.field.sortOrder,
      }));
    } else {
      query.filters = undefined;

      if (currentColumnSort) {
        query.sort = [
          {
            fieldName: currentColumnSort.fieldName,
            sortOrder: isNaN(currentColumnSort.sortOrder)
              ? 1
              : currentColumnSort.sortOrder,
          },
        ];
      }
    }
    query.filters =
      query.filters && query.filters.fields.length ? query.filters : undefined;
    query.sort = query.sort && query.sort.length ? query.sort : undefined;

    const flow = currentWorkflow();
    if (flow === 'auditStd') {
      const batch = auditBatch.data.getState().batch;
      query.auditId = batch ? batch.auditId : undefined;
    } else {
      query.auditId = undefined;
    }

    // console.log('refreshQuery', query);
    data.setState({ query });

    return query;
  };
  const setQuery = (query: VisitSearchQuery) => {
    data.setState({ query: { ...refreshQuery(), ...query } });
  };
  const openFilter = (selectedFilter: VisitSearchFieldCriteria) => {
    data.setState({ selectedFilter, openFilter: true });
  };
  const closeFilter = () => {
    data.setState({ openFilter: false });
  };
  const reset = () => {
    const filters = data.getState().filters;
    filters.forEach((v: VisitSearchFieldCriteria) => {
      // if (v.field.fieldName === worksheetStateField.fieldName) {
        
      // }
      const c = v?.form?.controls?.control;
      const {start, end} = v?.form?.controls;
      if (c) {
        c.clear();
        v.field.stringFilter = null
        v.field.startDate = null
        c.value.setState({ value: [] })
      }
      if(start) {
        start.value.setState({value : null})
      }
    });
    clearAllSortables();
    data.setState({ filters: [], sort: [] });
  };

  const removeServiceDate = () => {
    const list = data.getState().filters;
    const filters = list.filter((u) => u.label !== serviceDate.label);
    data.setState({ filters });
  };
  const setServiceDate = (range: DateRange) => {
    if (!range || !range.startDate || !range.endDate) {
      removeServiceDate();
    } else {
      serviceDate.startDate = range.startDate;
      serviceDate.endDate = range.endDate;

      serviceDate.field = {
        fieldName: 'wsSummaries.0.serviceDate',
        startDate: utcDateString(range.startDate),
        endDate: utcDateString(range.endDate),
      };
      updateOrAddFilter(serviceDate.field);
    }
  };

  function resetPage() {
    page.setState({ pageNumber: 0 });
    data.getState().query.pageNumber = 0;
  }

  function resetCriteria(v) {
    if (v) {
      if (v.reset) {
        v.reset();
      }
    }
  }

  function makeSortable() {
    const fields = data.getState().fields;
    const sort = data.getState().sort;
    let temp = [...fields];
    let tempHash = new Map();



    if (sort.length > 0) {


      for (let i = 0; i < sort.length; i++) {
        let sortField = sort[i].field.fieldName.split('.');
        temp.map(item => {
          let str = item.field.fieldName.split('.');
          if (sortField[0] != str[0] && str.length > 1 && sortField.length > 1 && sort[i].field.isArray && item.field.isArray) {
            item.field.sortable = false;
            if (sortField[0] === 'wsSummaries') {
              visitInsuranceField.sortable = false;
            } else if (sortField[0] === 'insurances') {
              serviceDateField.sortable = false;
              ownerLastField.sortable = false;
            } else {
              visitInsuranceField.sortable = true;
              serviceDateField.sortable = true;
              ownerLastField.sortable = true;
            }

            tempHash.set(item.field.fieldName, item.field.fieldName.split('.')[0]);
          } else {

            if (!tempHash.get(item.field.fieldName)) {

              item.field.sortable = true;

            } else {
              item.field.sortable = false;
            }
          }
        })

        if (tempHash.size === 0) {
          visitInsuranceField.sortable = true;
          serviceDateField.sortable = true;
          ownerLastField.sortable = true;
        }
        data.setState({ fields: temp })
      }
    } else {
      clearAllSortables();
    }


  }

  function clearAllSortables() {
    const fields = data.getState().fields;
    let temp = [...fields];
    temp.map(item => {
      item.field.sortable = true;
    })


    visitInsuranceField.sortable = true;
    serviceDateField.sortable = true;
    ownerLastField.sortable = true;

    data.setState({ fields: temp })
  }


  const actions: VisitSearchActions = {
    search,
    hasFilter,
    hasSort,
    setQuery,
    updateOrAddFilter,
    updateOrAddSort,
    openFilter,
    closeFilter,
    reset,
    setServiceDate,
    makeSortable,
    updateColumnSort: (v) => {
      currentColumnSort = v;
      return v;
    },
    deleteFilter: (v) => {
      const list = data.getState().filters;
      const filters = list.filter((u) => u.label !== v.label);
      data.setState({ ...{ filters }, ...refreshFields() });
      resetPage();
      search();
      resetCriteria(v);
    },
    addFilter: (v) => {
      const filters = data.getState().filters;
      if (!hasField(filters, v)) {
        data.setState({
          ...{ filters: filters.concat({ ...v }) },
          ...refreshFields(),
        });
      }
      resetPage();
      search();
      closeFilter();
    },
    updateFilter: (v) => {
      const filters = data.getState().filters;
      filters.forEach((u) => {
        if (u.label === v.label) {
          u.field = { ...u.field, ...v.field };
        }
      });
      resetPage();
      search();
    },
    deleteSort: (v) => {
      const list = data.getState().sort;
      const sort = list.filter((u) => u.label !== v.label);
      data.setState({ ...{ sort }, ...refreshFields() });
      makeSortable();
      search();
    },
    addSort: (v) => {
      const sort = data.getState().sort;


      if (!hasField(sort, v)) {
        data.setState({
          ...{ sort: sort.concat({ ...v }) },
          ...refreshFields(),
        });
      }
      makeSortable();
      search();
    },
    updateSort: (v) => {
      const sort = data.getState().sort;
      sort.forEach((u) => {
        if (u.label === v.label) {
          u.field.sortOrder = v.field.sortOrder;
        }
      });
      makeSortable();
      search();
    },
  };

  const predicate = formApi<{ predicate: SearchPredicate }>({
    values: { predicate: 'OR' },
    fields: {
      predicate: {
        style: { height: 48 },
        options: [
          { value: 'OR', label: 'Or' },
          { value: 'AND', label: 'And' },
        ],
      },
    },
    onChange: (v) => {
      currentPredicate = v.predicate;
      refreshQuery();
    },
  });

  const forms = { predicate };

  return {
    configuration,
    data,
    actions,
    forms,
  };
}
